import React from "react"

import { Skeleton, Tooltip } from "@coval/ui"
import { Copy, Trash2 } from "lucide-react"

import { cn } from "@/utils/cn"

import { formatDate } from "../../utils/filterSimulations"
import type { ExperimentTestResult } from "../types"
import { ReviewBadge } from "./ReviewBadge"

export interface RecentSimulationItem {
  id: string
  name: string
  description: string
  createdAt: string
  agentId?: string
  isImported?: boolean
  metricValue?: string | number
}

export type TestResult = ExperimentTestResult

interface RecentSimulationRowProps {
  simulation: RecentSimulationItem
  testResult: TestResult | null
  isEvaluating: boolean
  isActive?: boolean
  onRowClick: () => void
  onPointerEnter?: () => void
  onPointerLeave?: () => void
  onDelete: () => void
  metricLabel?: string
  showTestResult?: boolean
}

export const RecentSimulationRow: React.FC<RecentSimulationRowProps> = ({
  simulation,
  testResult,
  isEvaluating,
  isActive = false,
  onRowClick,
  onPointerEnter,
  onPointerLeave,
  onDelete,
  metricLabel,
  showTestResult = true,
}) => {
  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation()
    void navigator.clipboard.writeText(simulation.id)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete()
  }

  return (
    <tr
      className={cn(
        "border-b border-coval-border hover:bg-surface-1/50 cursor-pointer transition-colors group",
        isActive && "bg-surface-1/50",
      )}
      data-recent-simulation-id={simulation.id}
      onClick={onRowClick}
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}
    >
      <td className="px-4 py-4 text-left" style={{ verticalAlign: "middle" }}>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-coval-text-muted font-mono">
            {simulation.id.slice(0, 8)}
          </span>
          <Tooltip content="Copy ID" side="top">
            <button
              onClick={handleCopyId}
              aria-label="Copy ID"
              className="text-coval-text-muted hover:text-coval-text-muted transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
          {simulation.isImported && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-coval-contrast/10 text-coval-contrast">
              imported
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-4 text-left" style={{ verticalAlign: "top" }}>
        <span className="text-sm text-coval-text-muted">
          {formatDate(simulation.createdAt)}
        </span>
      </td>
      {metricLabel !== undefined && (
        <td className="px-4 py-4 text-left" style={{ verticalAlign: "top" }}>
          {simulation.metricValue != null ? (
            <ReviewBadge value={String(simulation.metricValue)} />
          ) : (
            <span className="text-xs text-coval-text-muted">—</span>
          )}
        </td>
      )}
      {showTestResult ? (
        <td className="px-4 py-4 text-left" style={{ verticalAlign: "top" }}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 items-center justify-center">
              {isEvaluating ? (
                <Skeleton className="w-full h-4 rounded-lg" />
              ) : testResult ? (
                <div className="flex items-start gap-2">
                  <ReviewBadge
                    value={testResult.value}
                    errorStatus={testResult.errorStatus}
                    errorMessage={testResult.errorMessage}
                  />
                  <span className="text-sm text-coval-text line-clamp-2">
                    {testResult.explanation}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-coval-text-muted">
                  Not tested
                </span>
              )}
            </div>
            <Tooltip content="Remove from list" side="top">
              <button
                onClick={handleDelete}
                aria-label="Remove from list"
                className="opacity-0 group-hover:opacity-100 text-coval-text-muted hover:text-coval-danger-text transition-all"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </Tooltip>
          </div>
        </td>
      ) : (
        <td
          className="px-4 py-4 text-right"
          style={{ verticalAlign: "middle" }}
        >
          <Tooltip content="Remove from list" side="top">
            <button
              onClick={handleDelete}
              className="opacity-0 group-hover:opacity-100 text-coval-text-muted hover:text-coval-danger-text transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </td>
      )}
    </tr>
  )
}
